import type { AudioRecordingObservable } from "@/store/audio-recordings";
import { calculateRecordingDuration } from "@/store/audio-recordings/app-events";
import {
  AudioRecordingRecordingStatus,
  type AudioRecordingModelData,
} from "@/store/audio-recordings/types";

type HandoffDurationTransition = {
  previousAudioRecordingId: string;
  successorAudioRecordingId: string;
  associatedNoteId: string;
} | null;

/**
 * Resolves the user-facing elapsed time for a logical voice session that may
 * span multiple local recording segments after a detected-meeting handoff.
 */
export function resolveAudioRecordingDurationLabel({
  activeRecording,
  noteRecordings,
  handoffTransition,
  noteId,
  fallbackDurationLabel,
}: {
  activeRecording: AudioRecordingObservable | null | undefined;
  noteRecordings: (AudioRecordingObservable | null | undefined)[];
  handoffTransition: HandoffDurationTransition | null | undefined;
  noteId: string | null | undefined;
  fallbackDurationLabel: string;
}): string {
  const durationRecordings = resolveAudioRecordingDurationGroup({
    activeRecording,
    noteRecordings,
    handoffTransition,
    noteId,
  });

  return resolveRecordingGroupDurationLabel({
    recordings: durationRecordings,
    fallbackDurationLabel,
  });
}

/**
 * During the transient restart state, the successor may be active before its
 * persisted handoff metadata reaches observables, so include the in-memory
 * transition as a source of truth for elapsed-time display.
 */
function resolveAudioRecordingDurationGroup({
  activeRecording,
  noteRecordings,
  handoffTransition,
  noteId,
}: {
  activeRecording: AudioRecordingObservable | null | undefined;
  noteRecordings: (AudioRecordingObservable | null | undefined)[];
  handoffTransition: HandoffDurationTransition | null | undefined;
  noteId: string | null | undefined;
}): AudioRecordingObservable[] {
  if (!activeRecording) return [];

  const transitionMatchesActiveRecording =
    handoffTransition &&
    handoffTransition.successorAudioRecordingId === activeRecording.id &&
    (!noteId || handoffTransition.associatedNoteId === noteId);

  if (transitionMatchesActiveRecording) {
    const previousRecording = noteRecordings.find(
      recording => recording?.id === handoffTransition.previousAudioRecordingId
    );

    return dedupeRecordings([previousRecording, activeRecording]);
  }

  const handoffPreviousAudioRecordingId = activeRecording.meetingHandoffPreviousAudioRecordingId;
  if (!handoffPreviousAudioRecordingId) return [activeRecording];

  const previousRecording = noteRecordings.find(
    recording => recording?.id === handoffPreviousAudioRecordingId
  );

  return dedupeRecordings([previousRecording, activeRecording]);
}

function resolveRecordingGroupDurationLabel({
  recordings,
  fallbackDurationLabel,
}: {
  recordings: AudioRecordingObservable[];
  fallbackDurationLabel: string;
}): string {
  let hasDuration = false;
  const totalMs = recordings.reduce((sum, recording) => {
    const durationMs = resolveRecordingDurationMs(recording);
    if (durationMs == null) return sum;

    hasDuration = true;
    return sum + durationMs;
  }, 0);

  if (!hasDuration) return fallbackDurationLabel;
  return formatDurationMs(totalMs);
}

function resolveRecordingDurationMs(recording: AudioRecordingObservable): number | null {
  const completedDurationMs = resolveCompletedRecordingDurationMs(recording);
  if (completedDurationMs !== null) return completedDurationMs;

  const durationMs = recording.durationMs;
  if (typeof durationMs === "number" && Number.isFinite(durationMs)) {
    return Math.max(0, durationMs);
  }

  return parseDurationLabelMs(recording.formattedDuration);
}

function resolveCompletedRecordingDurationMs(recording: AudioRecordingObservable): number | null {
  const modelData = (recording as { modelData?: AudioRecordingModelData }).modelData;
  if (modelData?.recording_status !== AudioRecordingRecordingStatus.COMPLETED) return null;

  const durationResult = calculateRecordingDuration({ modelData });
  if (!durationResult.success) return null;

  return Math.max(0, Math.round(durationResult.duration.as("milliseconds")));
}

function parseDurationLabelMs(durationLabel: string | null | undefined): number | null {
  if (!durationLabel) return null;

  const parts = durationLabel.split(":");
  if (parts.length < 2 || parts.length > 3) return null;

  const parsedParts = parts.map(part => Number(part));
  if (parsedParts.some(part => !Number.isInteger(part) || part < 0)) return null;

  const [hoursOrMinutes, minutesOrSeconds, secondsWhenHours] = parsedParts;
  const totalSeconds =
    secondsWhenHours == null
      ? hoursOrMinutes * 60 + minutesOrSeconds
      : hoursOrMinutes * 3600 + minutesOrSeconds * 60 + secondsWhenHours;

  return totalSeconds * 1000;
}

function formatDurationMs(durationMs: number): string {
  const totalSeconds = Math.floor(Math.max(0, durationMs) / 1000);
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;

  if (hours > 0) {
    return `${hours}:${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
  }

  return `${minutes.toString().padStart(2, "0")}:${seconds.toString().padStart(2, "0")}`;
}

function dedupeRecordings(
  recordings: (AudioRecordingObservable | null | undefined)[]
): AudioRecordingObservable[] {
  const seen = new Set<string>();
  return recordings.filter((recording): recording is AudioRecordingObservable => {
    if (!recording || seen.has(recording.id)) return false;

    seen.add(recording.id);
    return true;
  });
}
